import { useContext, useEffect, useMemo, useState } from 'react';
import { fetchEvents, createEvent, Event, CreateEventPayload } from '../services/apiClient';
import { AuthContext } from '../context/AuthContext';

const BLANK_FORM: CreateEventPayload = {
  name: '',
  date: '',
  time: '',
  location: '',
  description: '',
};

export const EventsList: React.FC = () => {
  const auth = useContext(AuthContext);

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateEventPayload>(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchEvents(50);
      setEvents(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load events';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [events]
  );

  const handleFieldChange = (field: keyof CreateEventPayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formMsg) setFormMsg(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormMsg({ type: 'error', text: 'Event name is required.' }); return; }
    if (!form.date) { setFormMsg({ type: 'error', text: 'Event date is required.' }); return; }

    setSaving(true);
    setFormMsg(null);
    try {
      const payload: CreateEventPayload = {
        name: form.name.trim(),
        date: form.date,
        createdByUserId: auth?.user?.id,
      };
      if (form.time?.trim()) payload.time = form.time.trim();
      if (form.location?.trim()) payload.location = form.location.trim();
      if (form.description?.trim()) payload.description = form.description.trim();

      const created = await createEvent(payload);
      setEvents((prev) => [created, ...prev]);
      setForm(BLANK_FORM);
      setShowForm(false);
      setFormMsg({ type: 'success', text: `Event "${created.name}" created.` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create event.';
      setFormMsg({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const canCreate = auth?.user?.role === 'admin' || auth?.user?.role === 'event_manager';

  return (
    <div className="table-wrap">
      <div className="table-header">
        <p className="table-title">Events</p>
        {canCreate && (
          <button
            className="btn-primary"
            onClick={() => { setShowForm((v) => !v); setFormMsg(null); }}
          >
            {showForm ? 'Cancel' : '+ Add Event'}
          </button>
        )}
      </div>

      {formMsg && (
        <p className={formMsg.type === 'success' ? 'msg-success' : 'msg-error'}>{formMsg.text}</p>
      )}

      {showForm && (
        <div className="member-form">
          <div className="member-form-grid">
            <label>
              <span>Event Name *</span>
              <input
                type="text"
                value={form.name}
                placeholder="e.g. Spring Luncheon"
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
            </label>
            <label>
              <span>Date *</span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => handleFieldChange('date', e.target.value)}
              />
            </label>
            <label>
              <span>Time</span>
              <input
                type="time"
                value={form.time}
                onChange={(e) => handleFieldChange('time', e.target.value)}
              />
            </label>
            <label>
              <span>Location</span>
              <input
                type="text"
                value={form.location}
                placeholder="e.g. Frisco City Hall"
                onChange={(e) => handleFieldChange('location', e.target.value)}
              />
            </label>
          </div>
          <label style={{ display: 'block', marginTop: '.75rem' }}>
            <span style={{ display: 'block', marginBottom: '.3rem', fontWeight: 600 }}>Description</span>
            <textarea
              rows={3}
              value={form.description}
              placeholder="Optional event details…"
              onChange={(e) => handleFieldChange('description', e.target.value)}
              style={{ width: '100%', padding: '.5rem .75rem', borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '.9rem', resize: 'vertical' }}
            />
          </label>
          <div className="member-form-actions">
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Event'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="status-loading">Loading events…</p>
      ) : error ? (
        <p className="status-error">Error: {error}</p>
      ) : sortedEvents.length === 0 ? (
        <p className="empty-state">No events found</p>
      ) : (
        <table className="list-table">
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Description</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedEvents.map((event) => (
              <tr key={event.id}>
                <td>{event.name || 'Untitled event'}</td>
                <td>{event.date ? new Date(event.date + 'T12:00:00').toLocaleDateString() : 'TBD'}</td>
                <td>{event.time ?? '—'}</td>
                <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {event.description ?? '—'}
                </td>
                <td>
                  <span className={`status-badge status-${event.isActive ? 'active' : 'inactive'}`}>
                    {event.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
